import { useState } from "react";
import { Result, ResultSummary } from "store/reducers/results/reducer";
import { User } from "store/reducers/users/reducer";
import { getDisplayNameOrEmail } from "store/reducers/users/utils";
import TastingInsights from "./tasting-insights";
import {
  AwardCard,
  AwardGrid,
  BestTasteCard,
  BestTasteItem,
  EmptyRow,
  NoResultsMessage,
  Pad,
  PadTop,
  PodiumPillar,
  PodiumRow,
  PodiumWrap,
  RankingCard,
  RankingItem,
  SectionHeader,
  SubTab,
  SubTabs,
} from "./tasting-results.styles";

type SubTabKey = "ranking" | "insights";

type TastingResultsProps = {
  results?: Result;
  users?: User[] | null;
};

const TastingResults = ({ results, users }: TastingResultsProps) => {
  const [sub, setSub] = useState<SubTabKey>("ranking");

  const available = Boolean(results?.lastUpdated);

  // Beer lover / hater — extremes of totalTaste across contestants
  let lover: ResultSummary | undefined;
  let hater: ResultSummary | undefined;
  results?.roundResults?.forEach((r) => {
    lover = r.totalTaste < (lover?.totalTaste || Number.MAX_SAFE_INTEGER) ? r : lover;
    hater = r.totalTaste > (hater?.totalTaste || -0) ? r : hater;
  });

  const display = (email?: string | null) => {
    if (!email) return "-";
    const user = users?.find((u) => u.email === email);
    return getDisplayNameOrEmail(user?.displayName, email);
  };

  if (!available) {
    return <NoResultsMessage />;
  }

  const roundResults = results?.roundResults ?? [];
  const beerScoreResults = results?.beerScoreResults ?? [];
  const resultsCount = roundResults.length;

  const podium = roundResults.slice(0, 3);
  const rest = roundResults.slice(3);

  const tasteMax = Math.max(...beerScoreResults.map((b) => b.points), 1);
  const tasteMin = Math.min(...beerScoreResults.map((b) => b.points), 0);
  const tasteRange = Math.max(1, tasteMax - tasteMin);

  return (
    <div>
      <SubTabs>
        <SubTab
          key={`ranking-${sub === "ranking" ? "on" : "off"}`}
          active={sub === "ranking"}
          onClick={() => setSub("ranking")}
        >
          Ranking
        </SubTab>
        <SubTab
          key={`insights-${sub === "insights" ? "on" : "off"}`}
          active={sub === "insights"}
          onClick={() => setSub("insights")}
        >
          Insights
        </SubTab>
      </SubTabs>

      {sub === "ranking" && (
        <>
          {/* Podium */}
          {podium.length > 0 && (
            <PodiumWrap>
              <SectionHeader label="Podium" />
              <PodiumRow>
                {/* Render order: 2nd, 1st, 3rd for visual pillar effect */}
                {[podium[1], podium[0], podium[2]]
                  .filter(Boolean)
                  .map((s, i) => {
                    const rank = roundResults.indexOf(s) + 1;
                    return (
                      <PodiumPillar
                        key={s.userEmail ?? i}
                        rank={rank}
                        name={display(s.userEmail)}
                        points={s.totalPoints}
                      />
                    );
                  })}
              </PodiumRow>
            </PodiumWrap>
          )}

          {/* Full ranking list */}
          {rest.length > 0 && (
            <PadTop>
              <SectionHeader label="Full Ranking" />
              <RankingCard>
                {rest.map((s, i) => (
                  <RankingItem
                    key={s.userEmail ?? i}
                    rank={i + 4}
                    name={display(s.userEmail)}
                    points={s.totalPoints}
                    isTied={s.isTied}
                    tieBreakerReason={s.tieBreakerReason}
                    isLast={i === rest.length - 1}
                  />
                ))}
              </RankingCard>
            </PadTop>
          )}

          {roundResults.length === 0 && (
            <Pad>
              <EmptyRow>No ranking results</EmptyRow>
            </Pad>
          )}

          {/* Best Taste */}
          <PadTop>
            <SectionHeader label="Best Taste" hint="Fewer points = more liked" />
            {beerScoreResults.length > 0 ? (
              <BestTasteCard>
                {beerScoreResults.map((b, i) => {
                  const pct = Math.max(
                    0,
                    Math.min(1, (tasteMax - b.points) / tasteRange),
                  );
                  return (
                    <BestTasteItem
                      key={b.name ?? i}
                      rank={i + 1}
                      name={b.name}
                      points={b.points}
                      pct={pct}
                      isLast={i === beerScoreResults.length - 1}
                    />
                  );
                })}
              </BestTasteCard>
            ) : (
              <EmptyRow>No taste score results</EmptyRow>
            )}
          </PadTop>

          {/* Awards */}
          {resultsCount > 1 && (
            <AwardGrid>
              <AwardCard
                tone="pos"
                emoji="😍"
                label="Beer Lover"
                name={display(lover?.userEmail)}
              />
              <AwardCard
                tone="neg"
                emoji="🤢"
                label="Beer Hater"
                name={display(hater?.userEmail)}
              />
            </AwardGrid>
          )}
        </>
      )}

      {sub === "insights" && (
        <TastingInsights results={results} users={users} />
      )}
    </div>
  );
};

export default TastingResults;
